import { useCallback, useEffect, useRef, useState } from 'react';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { FlexColumn } from 'ui-components/components/flex';
import { FlexRow } from 'ui-components/components/flex/row';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import styles from '../styles.module.less';
import { HtmlTemplatePreview } from './html-template-preview';
import { messages } from './messages';
import { useGetHtmlTemplatePreview } from './use-get-html-template-preview';
import { ZoomControls } from './zoom-controls/zoom-controls';
import { WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Banner } from '../components/banner';
import {
  PreviewTrigger,
  type ApiErrorDetails,
  type Layout,
  type Orientation,
  type TemplateType,
  type TrackWorkflowFunction,
} from '../types';

import type { ReactNode } from 'react';
import type { Observable } from 'rxjs';

const DEFAULT_ZOOM = 0.7;
const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.0;

type PreviewPanelProps = {
  templateType: TemplateType;
  contentChange$: Observable<string>;
  sourceIds: number[] | null;
  previewToolbar?: ReactNode;
  layout: Layout;
  orientation: Orientation;
  initialHtmlContent: string;
  initialPreviewUrl: string;
  hasInitialFetchError: boolean;
  hasValidationError: boolean;
  onApiValidationError: (apiError: ApiErrorDetails | null) => void;
  tracking: TrackWorkflowFunction;
};

export const PreviewPanel = ({
  templateType,
  contentChange$,
  sourceIds,
  previewToolbar,
  layout,
  orientation,
  initialHtmlContent,
  initialPreviewUrl,
  hasInitialFetchError,
  hasValidationError,
  onApiValidationError,
  tracking,
}: PreviewPanelProps) => {
  const htmlContentRef = useRef<string>(initialHtmlContent);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const isFirstRender = useRef(true);

  const { previewUrl, isLoading, fetchError, apiErrorDetails, refetch } = useGetHtmlTemplatePreview(
    templateType,
    initialPreviewUrl,
    hasInitialFetchError,
    tracking,
  );

  useEffect(() => {
    onApiValidationError(apiErrorDetails);
  }, [apiErrorDetails, onApiValidationError]);

  useEffect(() => {
    const subscription = contentChange$.subscribe((content) => {
      htmlContentRef.current = content;
      refetch(content, sourceIds, layout, orientation, PreviewTrigger.Automatic);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [contentChange$, sourceIds, layout, orientation, refetch]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (htmlContentRef.current) {
      refetch(htmlContentRef.current, sourceIds, layout, orientation, PreviewTrigger.Automatic);
    }
  }, [sourceIds, layout, orientation, refetch]);

  const isZoomInDisabled = zoom >= MAX_ZOOM;
  const isZoomOutDisabled = zoom <= MIN_ZOOM;

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => {
      const newZoom = Math.min(Math.round((prev + ZOOM_STEP) * 10) / 10, MAX_ZOOM);
      tracking(WorkflowSteps.ZOOM_IN, {
        fromZoom: prev,
        toZoom: newZoom,
      });
      return newZoom;
    });
  }, [tracking]);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => {
      const newZoom = Math.max(Math.round((prev - ZOOM_STEP) * 10) / 10, MIN_ZOOM);
      tracking(WorkflowSteps.ZOOM_OUT, {
        fromZoom: prev,
        toZoom: newZoom,
      });
      return newZoom;
    });
  }, [tracking]);

  const handleRefresh = useCallback(() => {
    setZoom(DEFAULT_ZOOM);
    refetch(htmlContentRef.current, sourceIds, layout, orientation, PreviewTrigger.Manual);
  }, [refetch, sourceIds, layout, orientation]);

  return (
    <FlexColumn className={styles['preview-section']}>
      <FlexRow className={styles['preview-toolbar']}>
        {previewToolbar}
        <ZoomControls
          isRefreshing={isLoading}
          isZoomInDisabled={isZoomInDisabled}
          isZoomOutDisabled={isZoomOutDisabled}
          onRefresh={handleRefresh}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          scale={zoom}
        />
      </FlexRow>
      <div className={styles['preview-area']}>
        {isLoading ? (
          <div className={styles['loading-container']}>
            <LoadingIndicator className={styles.loading} />
          </div>
        ) : null}
        {!isLoading && fetchError ? (
          <Banner
            content={<Text textType={TextType.Caption} value={messages.loadError} />}
            layout={BannerLayout.Inline}
            status={BannerStatus.Error}
          />
        ) : null}
        {!isLoading && !fetchError && hasValidationError ? (
          <Banner
            content={<Text textType={TextType.Caption} value={messages.validationError} />}
            layout={BannerLayout.Inline}
            status={BannerStatus.Warning}
          />
        ) : null}
        {!isLoading && !fetchError && !hasValidationError ? <HtmlTemplatePreview previewUrl={previewUrl} zoom={zoom} /> : null}
      </div>
    </FlexColumn>
  );
};
